import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CheckIcon from '@mui/icons-material/Check';
import OButton from '../../../UI/FormControls/OButton';
import { colors } from '../../../../../shared/colors';
import { icons } from '../../../../../shared/icons';
import useIzvestajRadnogNaloga from './useIzvestajRadnogNaloga';
import {
  noviRed,
  poljaZaglavlja,
  prevodIzvestaja,
  prikazCena,
  rekapitulacijaIzvestaja,
  sekcijeIzvestaja,
  trosakReda,
  vidljiveKoloneZa,
  VRSTA,
} from './generisiIzvestajPDF';

const SVETLO_NARANDZASTA = '#fff4ea';
const CRVENA = '#c62828';
const SIRINE_KOLONA = {
  [VRSTA.DA_NE]: 70,
  [VRSTA.BROJ]: 90,
  [VRSTA.IZNOS]: 130,
  [VRSTA.DATUM]: 120,
  [VRSTA.TROSAK]: 120,
};

const kopija = (vrednost) => JSON.parse(JSON.stringify(vrednost));
const jeBroj = (vrsta) => vrsta === VRSTA.BROJ || vrsta === VRSTA.IZNOS;
const sirinaKolone = (kolona) =>
  SIRINE_KOLONA[kolona.vrsta] || (kolona.dugacko ? 240 : 160);
const kljucKolone = (kolona) => kolona.polje || kolona.vrsta;

const narandzastiSwitch = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: colors.ORANGE },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: colors.ORANGE,
  },
};

const Prekidac = ({ checked, onChange, labela }) => (
  <FormControlLabel
    sx={{ mr: 0 }}
    control={
      <Switch
        size="small"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        sx={narandzastiSwitch}
      />
    }
    label={<Typography sx={{ fontSize: 13 }}>{labela}</Typography>}
  />
);

const Kartica = ({ naslov, desno, children, iskljucena }) => (
  <Paper
    variant="outlined"
    sx={{
      borderRadius: 2,
      mb: 2,
      overflow: 'hidden',
      opacity: iskljucena ? 0.6 : 1,
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
        px: 2,
        py: 1,
        borderBottom: iskljucena ? 'none' : '1px solid #eee',
        backgroundColor: '#fafafa',
      }}
    >
      <Box
        sx={{
          width: 4,
          height: 18,
          borderRadius: 1,
          backgroundColor: colors.ORANGE,
        }}
      />
      <Typography sx={{ fontWeight: 600, fontSize: 15, flex: 1 }}>
        {naslov}
      </Typography>
      {desno}
    </Box>
    {!iskljucena && children}
  </Paper>
);

const PoljeVrednosti = ({ vrsta, dugacko, vrednost, onChange }) => {
  if (vrsta === VRSTA.DA_NE) {
    return (
      <Checkbox
        size="small"
        checked={Boolean(vrednost)}
        onChange={(e) => onChange(e.target.checked)}
        sx={{ color: '#999', '&.Mui-checked': { color: colors.ORANGE } }}
      />
    );
  }
  const broj = jeBroj(vrsta);
  return (
    <TextField
      fullWidth
      size="small"
      variant="standard"
      value={vrednost ?? ''}
      type={broj ? 'number' : 'text'}
      multiline={dugacko}
      maxRows={4}
      onChange={(e) => onChange(e.target.value)}
      InputProps={{
        inputProps: {
          step: 'any',
          style: { fontSize: 13, textAlign: broj ? 'right' : 'left' },
        },
        endAdornment:
          vrsta === VRSTA.IZNOS ? (
            <InputAdornment position="end">€</InputAdornment>
          ) : undefined,
      }}
    />
  );
};

const RedIzmene = memo(
  ({ sekcija, red, kolone, p, obrisiTekst, onCelija, onObrisi }) => (
    <TableRow hover>
      {kolone.map((kolona) =>
        kolona.vrsta === VRSTA.TROSAK ? (
          <TableCell
            key="trosak"
            align="right"
            sx={{ color: CRVENA, fontSize: 13 }}
          >
            {p.formatBroj(trosakReda(sekcija, red))}
          </TableCell>
        ) : (
          <TableCell key={kolona.polje} sx={{ py: 0.5 }}>
            <PoljeVrednosti
              vrsta={kolona.vrsta}
              dugacko={kolona.dugacko}
              vrednost={red[kolona.polje]}
              onChange={(vrednost) =>
                onCelija(sekcija.id, red.kljucReda, kolona.polje, vrednost)
              }
            />
          </TableCell>
        ),
      )}
      <TableCell sx={{ py: 0.5 }}>
        <Tooltip title={obrisiTekst}>
          <IconButton
            size="small"
            onClick={() => onObrisi(sekcija.id, red.kljucReda)}
            sx={{ '&:hover': { color: colors.DELETE } }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  ),
);

const SekcijaIzmene = memo(
  ({
    sekcija,
    stanje,
    tip,
    interne,
    klijent,
    p,
    t,
    onCelija,
    onObrisi,
    onDodaj,
    onUkljuci,
  }) => {
    const { ukljuceno, redovi } = stanje;
    const kolone = useMemo(
      () => vidljiveKoloneZa(sekcija, tip, { interne, klijent }),
      [sekcija, tip, interne, klijent],
    );
    const imaTrosak = kolone.some((k) => k.vrsta === VRSTA.TROSAK);

    return (
      <Kartica
        naslov={p[sekcija.naslov]}
        iskljucena={!ukljuceno}
        desno={
          <>
            <Chip size="small" label={redovi.length} />
            <Prekidac
              checked={ukljuceno}
              onChange={(vrednost) => onUkljuci(sekcija.id, vrednost)}
              labela={t('pdfDialog.ukljuciSekciju')}
            />
            <Button
              size="small"
              startIcon={<AddIcon />}
              disabled={!ukljuceno}
              onClick={() => onDodaj(sekcija)}
              sx={{ color: colors.ORANGE, textTransform: 'none' }}
            >
              {t('pdfDialog.dodajRed')}
            </Button>
          </>
        }
      >
        {redovi.length ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {kolone.map((kolona) => (
                    <TableCell
                      key={kljucKolone(kolona)}
                      align={
                        jeBroj(kolona.vrsta) || kolona.vrsta === VRSTA.TROSAK
                          ? 'right'
                          : 'left'
                      }
                      sx={{
                        minWidth: sirinaKolone(kolona),
                        fontWeight: 600,
                        fontSize: 12,
                        color: '#555',
                      }}
                    >
                      {p[kolona.header]}
                    </TableCell>
                  ))}
                  <TableCell sx={{ width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {redovi.map((red) => (
                  <RedIzmene
                    key={red.kljucReda}
                    sekcija={sekcija}
                    red={red}
                    kolone={kolone}
                    p={p}
                    obrisiTekst={t('pdfDialog.obrisiRed')}
                    onCelija={onCelija}
                    onObrisi={onObrisi}
                  />
                ))}
              </TableBody>
              {imaTrosak && (
                <TableFooter>
                  <TableRow sx={{ backgroundColor: SVETLO_NARANDZASTA }}>
                    {kolone.map((kolona, index) => (
                      <TableCell
                        key={kljucKolone(kolona)}
                        align={kolona.vrsta === VRSTA.TROSAK ? 'right' : 'left'}
                        sx={{
                          fontWeight: 600,
                          fontSize: 13,
                          color:
                            kolona.vrsta === VRSTA.TROSAK ? CRVENA : '#333',
                        }}
                      >
                        {kolona.vrsta === VRSTA.TROSAK &&
                          p.formatBroj(
                            redovi.reduce(
                              (acc, red) => acc + trosakReda(sekcija, red),
                              0,
                            ),
                          )}
                        {index === 0 && p.ukupno}
                      </TableCell>
                    ))}
                    <TableCell />
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </Box>
        ) : (
          <Typography sx={{ fontSize: 13, color: 'text.secondary', p: 2 }}>
            {p.nemaPodataka}
          </Typography>
        )}
      </Kartica>
    );
  },
);

const ZaglavljeIzmene = memo(({ polja, zaglavlje, p, naslov, onIzmeni }) => (
  <Kartica naslov={naslov}>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
        gap: 2,
        p: 2,
      }}
    >
      {polja.map((polje) =>
        polje.vrsta === VRSTA.DA_NE ? (
          <Box key={polje.kljuc} sx={{ display: 'flex', alignItems: 'center' }}>
            <Prekidac
              checked={Boolean(zaglavlje[polje.kljuc])}
              onChange={(vrednost) => onIzmeni(polje.kljuc, vrednost)}
              labela={p[polje.labela]}
            />
          </Box>
        ) : (
          <TextField
            key={polje.kljuc}
            size="small"
            label={p[polje.labela]}
            value={zaglavlje[polje.kljuc] ?? ''}
            multiline={polje.dugacko}
            minRows={polje.dugacko ? 2 : undefined}
            maxRows={8}
            onChange={(e) => onIzmeni(polje.kljuc, e.target.value)}
            sx={polje.dugacko ? { gridColumn: '1 / -1' } : undefined}
          />
        ),
      )}
    </Box>
  </Kartica>
));

const IzmenaIzvestajaDialog = ({ izvestaj, jezik, onClose, onPrimeni }) => {
  const { t } = useTranslation();
  const { pregledIzvestaja, isLoading } = useIzvestajRadnogNaloga();
  const [model, setModel] = useState(null);
  const [pregledUrl, setPregledUrl] = useState(null);
  const p = useMemo(() => prevodIzvestaja(jezik), [jezik]);

  useEffect(() => {
    setModel(izvestaj ? kopija(izvestaj) : null);
  }, [izvestaj]);

  const izmeniZaglavlje = useCallback(
    (kljuc, vrednost) =>
      setModel((m) => ({
        ...m,
        zaglavlje: { ...m.zaglavlje, [kljuc]: vrednost },
      })),
    [],
  );
  const izmeniSekciju = useCallback(
    (id, izmena) =>
      setModel((m) => ({
        ...m,
        sekcije: {
          ...m.sekcije,
          [id]: { ...m.sekcije[id], ...izmena(m.sekcije[id]) },
        },
      })),
    [],
  );
  const izmeniCeliju = useCallback(
    (id, kljucReda, polje, vrednost) =>
      izmeniSekciju(id, (s) => ({
        redovi: s.redovi.map((red) =>
          red.kljucReda === kljucReda ? { ...red, [polje]: vrednost } : red,
        ),
      })),
    [izmeniSekciju],
  );
  const obrisiRed = useCallback(
    (id, kljucReda) =>
      izmeniSekciju(id, (s) => ({
        redovi: s.redovi.filter((red) => red.kljucReda !== kljucReda),
      })),
    [izmeniSekciju],
  );
  const dodajRed = useCallback(
    (sekcija) =>
      izmeniSekciju(sekcija.id, (s) => ({
        redovi: [...s.redovi, noviRed(sekcija)],
      })),
    [izmeniSekciju],
  );
  const ukljuciSekciju = useCallback(
    (id, ukljuceno) => izmeniSekciju(id, () => ({ ukljuceno })),
    [izmeniSekciju],
  );

  const zatvoriPregled = () => {
    if (pregledUrl) URL.revokeObjectURL(pregledUrl);
    setPregledUrl(null);
  };

  if (!model) return null;

  const cene = prikazCena(model);
  const ukupnoTrosak = rekapitulacijaIzvestaja(model).reduce(
    (acc, r) => acc + r.trosak,
    0,
  );

  const handlePregled = async () => {
    const url = await pregledIzvestaja(model, jezik);
    if (url) setPregledUrl(url);
  };

  return (
    <>
      <Dialog
        open
        onClose={isLoading ? undefined : onClose}
        fullWidth
        maxWidth="xl"
        PaperProps={{ sx: { borderRadius: 3, height: '92vh' } }}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 7 }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: SVETLO_NARANDZASTA,
              color: colors.ORANGE,
            }}
          >
            <EditNoteIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
              {t('pdfDialog.izmenaNaslov')}
              {model.zaglavlje.ceoBrDok ? ` – ${model.zaglavlje.ceoBrDok}` : ''}
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {t('pdfDialog.izmenaOpis')}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={isLoading}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{ borderColor: '#eee', backgroundColor: '#f6f6f6' }}
        >
          <Kartica
            naslov={t('pdfDialog.opste')}
            desno={
              cene.interne && (
                <Chip
                  label={`${p.ukupnoTrosak}: ${p.formatBroj(ukupnoTrosak)}`}
                  sx={{
                    fontWeight: 600,
                    color: CRVENA,
                    backgroundColor: '#fdecea',
                  }}
                />
              )
            }
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, p: 2 }}>
              {model.dozvoljeneCene && (
                <Prekidac
                  checked={model.prikaziCene}
                  onChange={(vrednost) =>
                    setModel((m) => ({ ...m, prikaziCene: vrednost }))
                  }
                  labela={t('pdfDialog.prikaziCene')}
                />
              )}
              {cene.interne && (
                <Prekidac
                  checked={model.prikaziRekapitulaciju}
                  onChange={(vrednost) =>
                    setModel((m) => ({ ...m, prikaziRekapitulaciju: vrednost }))
                  }
                  labela={p.rekapitulacija}
                />
              )}
              {!model.dozvoljeneCene && (
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                  {t('pdfDialog.bezCena')}
                </Typography>
              )}
            </Box>
          </Kartica>

          <ZaglavljeIzmene
            polja={poljaZaglavlja(model.tip)}
            zaglavlje={model.zaglavlje}
            p={p}
            naslov={t('pdfDialog.zaglavlje')}
            onIzmeni={izmeniZaglavlje}
          />

          {sekcijeIzvestaja(model.tip).map((sekcija) => (
            <SekcijaIzmene
              key={sekcija.id}
              sekcija={sekcija}
              stanje={model.sekcije[sekcija.id]}
              tip={model.tip}
              interne={cene.interne}
              klijent={cene.klijent}
              p={p}
              t={t}
              onCelija={izmeniCeliju}
              onObrisi={obrisiRed}
              onDodaj={dodajRed}
              onUkljuci={ukljuciSekciju}
            />
          ))}

          <Kartica naslov={p.dodatnaNapomena}>
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={12}
                size="small"
                value={model.dodatnaNapomena}
                placeholder={t('pdfDialog.dodatnaNapomenaPlaceholder')}
                onChange={(e) =>
                  setModel((m) => ({ ...m, dodatnaNapomena: e.target.value }))
                }
              />
            </Box>
          </Kartica>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <OButton
            text={t('pdfDialog.otkazi')}
            startIcon={icons.DIALOG_CLOSE}
            onClick={onClose}
            disabled={isLoading}
            style={{
              color: colors.ORANGE,
              backgroundColor: colors.WHITE,
              border: `1px solid ${colors.ORANGE}`,
            }}
          />
          <OButton
            text={t('pdfDialog.pregled')}
            startIcon={<VisibilityOutlinedIcon htmlColor={colors.ORANGE} />}
            onClick={handlePregled}
            isLoading={isLoading}
            disabled={isLoading}
            style={{
              color: colors.ORANGE,
              backgroundColor: SVETLO_NARANDZASTA,
              border: `1px solid ${colors.ORANGE}`,
            }}
          />
          <OButton
            text={t('pdfDialog.primeniIzmene')}
            startIcon={<CheckIcon htmlColor={colors.WHITE} />}
            onClick={() => onPrimeni(model)}
            disabled={isLoading}
            style={{
              color: colors.WHITE,
              backgroundColor: colors.ORANGE,
              border: `1px solid ${colors.ORANGE}`,
            }}
          />
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pregledUrl)}
        onClose={zatvoriPregled}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { borderRadius: 3, height: '92vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 7 }}>
          {t('pdfDialog.pregled')}
          <IconButton
            onClick={zatvoriPregled}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {pregledUrl && (
            <iframe
              title={t('pdfDialog.pregled')}
              src={pregledUrl}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default IzmenaIzvestajaDialog;
